import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader, Play, RefreshCw, Sparkles } from 'lucide-react';
import {
  getLatestMassEnrichmentRun,
  previewMassEnrichment,
  runMassEnrichment,
  type MassEnrichmentPreview,
  type MassEnrichmentRun,
} from '../../../utils/massEnrichmentApi';

const PAGE_SIZES = [15, 20, 25, 30] as const;

export function MassEnrichmentPage() {
  const navigate = useNavigate();
  const [language, setLanguage] = useState('fr');
  const [pageSize, setPageSize] = useState<number>(20);
  const [offset, setOffset] = useState(0);
  const [preview, setPreview] = useState<MassEnrichmentPreview | null>(null);
  const [latest, setLatest] = useState<MassEnrichmentRun | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPreview = async (nextOffset = offset) => {
    setBusy(true);
    setError(null);
    try {
      const [prevRes, latestRes] = await Promise.all([
        previewMassEnrichment({ language, limit: pageSize, offset: nextOffset }),
        getLatestMassEnrichmentRun(),
      ]);
      setPreview(prevRes.preview);
      setLatest(latestRes.run);
      setOffset(nextOffset);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    loadPreview(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, pageSize]);

  const startRun = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await runMassEnrichment({ language, limit: pageSize, offset });
      navigate(`/admin/mass-db/enrichment/review?runId=${res.report.runId}`);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-menioo-navy flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-menioo-green" />
          Mass Enrichment
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Une file unique : CIQUAL + conversion d&apos;unités. Les ingrédients déjà complets sont
          exclus automatiquement.
        </p>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-4">
        <label className="text-sm">
          <span className="block text-gray-600 mb-1">Langue</span>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            disabled={busy}
          >
            <option value="fr">FR</option>
            <option value="en">EN</option>
            <option value="all">Toutes</option>
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-gray-600 mb-1">Taille de page</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            disabled={busy}
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={() => loadPreview(offset)}
          disabled={busy}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} />
          Rafraîchir
        </button>
        <button
          type="button"
          onClick={startRun}
          disabled={busy || !preview?.eligibleTotal}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-menioo-green rounded-md hover:opacity-90 disabled:opacity-50"
        >
          {busy ? <Loader className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          Lancer la page ({offset + 1}–{Math.min(offset + pageSize, preview?.eligibleTotal || 0)})
        </button>
      </div>

      {preview && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'À enrichir', value: preview.eligibleTotal },
            { label: 'Déjà complets', value: preview.skippedComplete },
            { label: 'Manque CIQUAL', value: preview.needCiqual },
            { label: 'Manque conversion', value: preview.needConversion },
          ].map((stat) => (
            <div key={stat.label} className="rounded-lg border border-gray-200 bg-white px-4 py-3">
              <div className="text-2xl font-semibold text-menioo-navy">{stat.value}</div>
              <div className="text-xs text-gray-500 mt-0.5">{stat.label}</div>
            </div>
          ))}
        </div>
      )}

      {preview && preview.needBoth > 0 && (
        <p className="text-sm text-gray-600">
          Dont <span className="font-medium text-menioo-navy">{preview.needBoth}</span> sans CIQUAL
          ni conversion.
        </p>
      )}

      {preview && preview.pagePreview.length > 0 && (
        <div className="rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <span className="text-sm font-medium text-menioo-navy">
              Aperçu page {Math.floor(offset / pageSize) + 1}
              {preview.pageCount ? ` / ${preview.pageCount}` : ''}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={busy || offset <= 0}
                onClick={() => loadPreview(Math.max(0, offset - pageSize))}
                className="text-sm px-2 py-1 border rounded disabled:opacity-40"
              >
                ← Préc.
              </button>
              <button
                type="button"
                disabled={busy || preview.nextOffset == null}
                onClick={() => preview.nextOffset != null && loadPreview(preview.nextOffset)}
                className="text-sm px-2 py-1 border rounded disabled:opacity-40"
              >
                Suiv. →
              </button>
            </div>
          </div>
          <ul className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
            {preview.pagePreview.map((row) => (
              <li key={row.id} className="px-4 py-2 text-sm flex items-center justify-between gap-3">
                <span className="font-medium text-menioo-navy truncate">{row.name}</span>
                <span className="text-xs text-gray-500 shrink-0 flex gap-2">
                  {row.needsCiqual && (
                    <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">CIQUAL</span>
                  )}
                  {row.needsConversion && (
                    <span className="px-1.5 py-0.5 rounded bg-sky-50 text-sky-700">
                      {row.conversionKind === 'density' ? 'densité' : 'poids unit.'}
                    </span>
                  )}
                  <span className="text-gray-400">{row.preferredUnit || '—'}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {latest?.id && (
        <p className="text-sm text-gray-500">
          Dernier run :{' '}
          <Link
            to={`/admin/mass-db/enrichment/review?runId=${latest.id}`}
            className="text-menioo-green hover:underline"
          >
            ouvrir la revue
          </Link>
          {latest.queued != null && ` · ${latest.queued} en file`}
          {latest.nextOffset != null && ` · suite offset ${latest.nextOffset}`}
        </p>
      )}
    </div>
  );
}
